import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

/**
 * @typedef {Object} MoveToFloor
 * @property {string} id - Unique identifier for the floor
 * @property {string} label - Display label for the floor
 * @property {string} type - Type identifier, always "floor"
 */

/**
 * @typedef {Object} MoveToBuilding
 * @property {string} id - Unique identifier for the building
 * @property {string} label - Display label for the building
 * @property {string} type - Type identifier, always "building"
 * @property {Array<MoveToFloor>} [floors] - Floors within the building
 */

/**
 * @typedef {Object} MoveToDropdownProps
 * @property {Array<MoveToBuilding>} buildings - Building items with floors array
 * @property {Function} onSelect - Callback with building ID (string) or null for Inbox
 * @property {Function} onClose - Callback to close the dropdown
 */

/**
 * Tree dropdown for moving files to a building.
 * Shows Inbox at top, then a searchable building/floor tree.
 * Floors are shown as non-interactive context (grayed, with tooltip).
 * Uses counter ref debounce pattern for click-outside
 * (see docs/gotchas/click-outside-immediate-close.md).
 *
 * @param {MoveToDropdownProps} props
 * @returns {React.ReactElement} The dropdown component
 */
export function MoveToDropdown({ buildings, onSelect, onClose }) {
  const dropdownRef = useRef(null)
  const clickCountRef = useRef(0)
  const [searchText, setSearchText] = useState("")
  const [expandedIds, setExpandedIds] = useState([])

  // Reset click counter on mount (debounce opening click)
  useEffect(() => {
    clickCountRef.current = 0
  }, [])

  // Click-outside handler with debounce
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Skip the first click (the one that opened us)
      if (clickCountRef.current < 1) {
        clickCountRef.current = 1
        return
      }

      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [onClose])

  const filteredBuildings = useMemo(() => {
    if (!searchText.trim()) return buildings
    const lower = searchText.toLowerCase()
    return buildings.filter(b => b.label.toLowerCase().includes(lower))
  }, [buildings, searchText])

  // Auto-expand buildings when search is active
  const displayExpandedIds = useMemo(() => {
    if (searchText.trim()) return filteredBuildings.map(b => b.id)
    return expandedIds
  }, [searchText, filteredBuildings, expandedIds])

  const toggleExpand = useCallback((buildingId) => {
    setExpandedIds(prev =>
      prev.includes(buildingId)
        ? prev.filter(id => id !== buildingId)
        : [...prev, buildingId]
    )
  }, [])

  const handleSelect = useCallback((buildingId) => {
    onSelect(buildingId)
    // Parent is responsible for closing -- do NOT call onClose() here
    // to avoid double-close (BulkActionBar's onSelect wrapper already closes)
  }, [onSelect])

  return (
    <div className="move-to-dropdown" ref={dropdownRef} data-testid="move-to-dropdown">
      <input
        type="text"
        className="move-to-search"
        placeholder="Search buildings..."
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        autoFocus
      />
      <div className="move-to-tree" role="listbox" aria-label="Move to building">
        <button
          type="button"
          className="move-to-item move-to-inbox"
          role="option"
          onClick={() => handleSelect(null)}
        >
          Inbox
        </button>
        {filteredBuildings.map(building => {
          const isExpanded = displayExpandedIds.includes(building.id)
          const hasFloors = Array.isArray(building.floors) && building.floors.length > 0
          return (
            <div key={building.id}>
              <div className="move-to-building-row">
                {hasFloors && (
                  <button
                    type="button"
                    className="move-to-expand-btn"
                    aria-label={`Toggle ${building.label}`}
                    aria-expanded={isExpanded}
                    onClick={() => toggleExpand(building.id)}
                  >
                    {isExpanded ? "\u25BE" : "\u25B8"}
                  </button>
                )}
                <button
                  type="button"
                  className="move-to-item"
                  role="option"
                  onClick={() => handleSelect(building.id)}
                >
                  {building.label}
                </button>
              </div>
              {isExpanded && hasFloors && building.floors.map(floor => (
                <div key={floor.id} className="move-to-floor-row">
                  <span
                    className="move-to-item move-to-floor"
                    aria-hidden="true"
                    title="Files are assigned to buildings, not individual floors"
                  >
                    {floor.label}
                  </span>
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
